import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaFileAlt, FaTimes } from "react-icons/fa";

export default function LegalDocumentEdit({ initialData, onSubmit }) {
  const [content, setContent] = useState(initialData?.content || "");
  const [showRawHTML, setShowRawHTML] = useState(false);
  const [rawHTML, setRawHTML] = useState(initialData?.content || "");
  const [documentFile, setDocumentFile] = useState(null);
  const [existingUrl, setExistingUrl] = useState(
    initialData?.documentUrl || null
  );
  const [validationError, setValidationError] = useState("");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData?.name || "",
      content: initialData?.content || "",
    },
  });

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["link"],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "align",
    "code-block",
  ];

  const toggleHTMLMode = () => {
    if (!showRawHTML) {
      setRawHTML(content);
    } else {
      setContent(rawHTML);
    }
    setShowRawHTML(!showRawHTML);
  };

  const handleRawHTMLChange = (value) => {
    setRawHTML(value);
  };

  const applyHTMLFormatting = () => {
    setContent(rawHTML);
    setShowRawHTML(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentFile(file);
      setExistingUrl(null);
      setValidationError("");
    }
  };

  const removeFile = () => {
    setDocumentFile(null);
    setExistingUrl(null);
  };

  const isContentEmpty = (value) => {
    if (!value) return true;
    const stripped = value.replace(/<[^>]*>/g, "").trim();
    return stripped.length === 0;
  };

  const submitForm = (data) => {
    const finalContent = showRawHTML ? rawHTML : content;
    const hasContent = !isContentEmpty(finalContent);
    const hasDocument = !!documentFile || !!existingUrl;

    if (!hasContent && !hasDocument) {
      setValidationError("Either content or a document file is required");
      return;
    }

    setValidationError("");

    const formData = new FormData();
    formData.append("name", data.name);
    if (hasContent) {
      formData.append("content", finalContent);
    }
    if (documentFile) {
      formData.append("document", documentFile);
    }
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4 p-2">
      {/* Document Name */}
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Document Name
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: true })}
          placeholder="Enter document name"
          className="form-input border border-mediumgray"
        />
        {errors.name && (
          <p className="form-error-message">Document name is required</p>
        )}
      </div>

      {/* Document File Upload */}
      <div className="space-y-2">
        <label className="form-label">Document File</label>
        {existingUrl && !documentFile ? (
          <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md">
            <a
              href={existingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-blue-600 hover:underline"
            >
              <FaFileAlt className="mr-2" />
              View current document
            </a>
            <button
              type="button"
              onClick={removeFile}
              className="text-gray-400 hover:text-red-500"
            >
              <FaTimes />
            </button>
          </div>
        ) : documentFile ? (
          <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md">
            <span className="flex items-center text-sm">
              <FaFileAlt className="mr-2" />
              {documentFile.name}
            </span>
            <button
              type="button"
              onClick={removeFile}
              className="text-gray-400 hover:text-red-500"
            >
              <FaTimes />
            </button>
          </div>
        ) : (
          <input
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx"
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
          />
        )}
      </div>

      {/* Content with ReactQuill and HTML Mode Toggle */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label htmlFor="content" className="form-label">
            Content
          </label>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={toggleHTMLMode}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300"
            >
              {showRawHTML ? "Visual Editor" : "HTML Source"}
            </button>
            {showRawHTML && (
              <button
                type="button"
                onClick={applyHTMLFormatting}
                className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md border border-blue-300"
              >
                Apply HTML
              </button>
            )}
          </div>
        </div>

        <div className="mt-1">
          {showRawHTML ? (
            <textarea
              value={rawHTML}
              onChange={(e) => handleRawHTMLChange(e.target.value)}
              placeholder='Paste your HTML here...
Example:
<h2>Your Title</h2>
<p>Your <strong>bold text</strong> here</p>'
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none text-sm font-mono"
              rows={15}
            />
          ) : (
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={(value) => {
                    setContent(value);
                    field.onChange(value);
                    setValidationError("");
                  }}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter document content... or switch to HTML Source to paste raw HTML"
                  style={{
                    height: "400px",
                    marginBottom: "50px",
                    fontFamily: "Inter",
                  }}
                />
              )}
            />
          )}
        </div>
      </div>

      {validationError && (
        <p className="form-error-message">{validationError}</p>
      )}

      {showRawHTML && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Paste your raw HTML above. Supported tags
            include: h1-h6, p, strong, em, ul, ol, li, blockquote, a, etc. Click
            "Apply HTML" to see the formatted result in the visual editor.
          </p>
        </div>
      )}

      <button type="submit" className="w-full black-button mt-4">
        {initialData?.name ? "Update Document" : "Create Document"}
      </button>
    </form>
  );
}